import { createReducer, on } from '@ngrx/store';
import { Wallet, Facturation } from '@core/models/wallet.model';
import * as WalletActions from './wallet.actions';

export interface WalletData {
    clientId: string;
    advertiserName: string;
    grandCompteId: number;
    wallet: Wallet | null;
}

export interface FacturationData {
    clientId: string;
    advertiserName: string;
    grandCompteId: number;
    facturation: Facturation | null;
}

export interface WalletState {
    walletsMap: { [key: string]: WalletData };
    facturationsMap: { [key: string]: FacturationData };
    currentWallet: WalletData | null;
    currentFacturation: FacturationData | null;
    loading: boolean;
    error: string | null;
}

export const initialState: WalletState = {
    walletsMap: {},
    facturationsMap: {},
    currentWallet: null,
    currentFacturation: null,
    loading: false,
    error: null,
};

export const walletReducer = createReducer(
    initialState,

    // Load wallets
    on(WalletActions.loadWallets, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),

    on(WalletActions.loadWalletsSuccess, (state, { wallets }) => {
        const newWalletsMap = { ...state.walletsMap };

        wallets.forEach((walletData) => {
            const key = `${walletData.grandCompteId}_${walletData.clientId}`;
            newWalletsMap[key] = walletData;
        });

        return {
            ...state,
            walletsMap: newWalletsMap,
            loading: false,
            error: null,
        };
    }),

    on(WalletActions.loadWalletsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),

    // Load wallet balance
    on(WalletActions.loadWalletBalance, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),

    on(WalletActions.loadWalletBalanceSuccess, (state, { walletId, balance }) => {
        // Find and update the wallet with matching id
        const newWalletsMap = { ...state.walletsMap };

        Object.keys(newWalletsMap).forEach((key) => {
            if (newWalletsMap[key].wallet && newWalletsMap[key].wallet!.id.toString() === walletId) {
                newWalletsMap[key] = {
                    ...newWalletsMap[key],
                    wallet: {
                        ...newWalletsMap[key].wallet!,
                        balance,
                    },
                };
            }
        });

        return {
            ...state,
            walletsMap: newWalletsMap,
            loading: false,
            error: null,
        };
    }),

    on(WalletActions.loadWalletBalanceFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),

    // Load facturations
    on(WalletActions.loadFacturations, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),

    on(WalletActions.loadFacturationsSuccess, (state, { facturations }) => {
        const newFacturationsMap = { ...state.facturationsMap };

        facturations.forEach((facturationData) => {
            const key = `${facturationData.grandCompteId}_${facturationData.clientId}`;
            newFacturationsMap[key] = facturationData;
        });

        return {
            ...state,
            facturationsMap: newFacturationsMap,
            loading: false,
            error: null,
        };
    }),

    on(WalletActions.loadFacturationsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),

    // Update facturation
    on(WalletActions.updateFacturation, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),

    on(WalletActions.updateFacturationSuccess, (state, { clientId, facturation }) => {
        const newFacturationsMap = { ...state.facturationsMap };

        // Find the key that matches this clientId
        const key = Object.keys(newFacturationsMap).find((k) => newFacturationsMap[k].clientId === clientId);

        if (key) {
            newFacturationsMap[key] = {
                ...newFacturationsMap[key],
                facturation,
            };
        }

        return {
            ...state,
            facturationsMap: newFacturationsMap,
            loading: false,
            error: null,
        };
    }),

    on(WalletActions.updateFacturationFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),

    // Set current wallet
    on(WalletActions.setCurrentWallet, (state, { clientId, grandCompteId }) => {
        const key = `${grandCompteId}_${clientId}`;
        const walletData = state.walletsMap[key];
        const facturationData = state.facturationsMap[key];

        return {
            ...state,
            currentWallet: walletData || null,
            currentFacturation: facturationData || null,
        };
    }),

    // Set current facturation
    on(WalletActions.setCurrentFacturation, (state, { clientId, grandCompteId }) => {
        const key = `${grandCompteId}_${clientId}`;
        const facturationData = state.facturationsMap[key];

        return {
            ...state,
            currentFacturation: facturationData || null,
        };
    }),

    // Clear all data
    on(WalletActions.clearWalletsAndFacturations, () => ({
        ...initialState,
    })),
);
